'use client';

import { useMemo, useState } from 'react';
import { textToAsciiArt } from '@/lib/browser-svggen-extra-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

const SPACING_OPTIONS = [
  { value: '1', label: 'Tight' },
  { value: '2', label: 'Normal' },
  { value: '3', label: 'Wide' },
];

export default function BrowserTextToAsciiArt() {
  const [text, setText] = useState('CLEANOR');
  const [spacing, setSpacing] = useState('2');
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const art = useMemo(
    () => textToAsciiArt(text.slice(0, 40), Number.parseInt(spacing, 10)),
    [text, spacing],
  );

  async function handleCopy() {
    try {
      await copyText(art);
      setCopyMessage('ASCII art copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">ASCII art</span>
        <h2 className="tool-live-title">Text to ASCII art</h2>
        <p className="tool-live-subtitle">
          Turn a word or short phrase into a block-letter ASCII banner for READMEs, terminals, code
          comments, and commit messages. Built-in font, rendered instantly in your browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>Text (up to 40 characters)</span>
              <input onChange={(e) => setText(e.target.value)} type="text" value={text} />
            </label>
            <label className="tool-live-field">
              <span>Letter spacing</span>
              <select onChange={(e) => setSpacing(e.target.value)} value={spacing}>
                {SPACING_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={handleCopy} type="button">
              Copy ASCII art
            </button>
          </div>
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <span
            style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}
          >
            Live preview
          </span>
          <pre
            style={{
              background: '#0f172a',
              color: '#e2e8f0',
              borderRadius: '12px',
              padding: '20px',
              margin: 0,
              overflowX: 'auto',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
              fontSize: '13px',
              lineHeight: 1.2,
              whiteSpace: 'pre',
            }}
          >
            {art || ' '}
          </pre>
          <label className="tool-live-field tool-live-field-full" style={{ marginTop: '16px' }}>
            <span>ASCII output</span>
            <textarea
              readOnly
              rows={7}
              style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' }}
              value={art}
            />
          </label>
          <p className="tool-live-panel-note">
            Generated locally in your browser. Nothing is uploaded.
          </p>
        </section>
      </div>
    </div>
  );
}
